import { Component, OnDestroy, OnInit, HostBinding, HostListener, ViewEncapsulation } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';

import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { AuthenticationService } from 'app/auth/service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreMediaService } from '@core/services/media.service';

import { NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { ShepherdService } from 'angular-shepherd';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoginComponent } from 'app/common/login/login.component';
import { AboutUsComponent } from 'app/common/about-us/about-us.component';
@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NavbarComponent implements OnInit, OnDestroy {
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;
  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;
  public defaultRouting: string = '/';
  public currentUser: any;

  public languageOptions: any;
  public navigation: any;
  public selectedLanguage: any;

  public menuStatus = null;

  public showIcon: boolean = false;

  public avatarImage: string = 'assets/images/portrait/small/unisexprofile.jpg';

  @HostBinding('class.fixed-top')
  public isFixed = false;

  @HostBinding('class.navbar-static-style-on-scroll')
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (
      (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == 'navbar-static-top'
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreMediaService} _coreMediaService
   * @param {MediaObserver} _mediaObserver
   * @param {TranslateService} _translateService
   * @param {DataService} _dataService
   */
  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private _mediaObserver: MediaObserver,
    public _translateService: TranslateService,
    private _coreMenuService: CoreMenuService,
    private _shepherdService: ShepherdService,
    private modalService: NgbModal
  ) {
    this.languageOptions = {
      en: {
        title: 'English',
        flag: 'us'
      },
      fr: {
        title: 'French',
        flag: 'fr'
      },
      de: {
        title: 'German',
        flag: 'de'
      },
      pt: {
        title: 'Portuguese',
        flag: 'pt'
      }
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  /**
   * Set the language
   *
   * @param language
   */
  setLanguage(language): void {
    // Set the selected language for the navbar on change
    this.selectedLanguage = language;

    // Use the selected language id for translations
    this._translateService.use(language);
  }

  /**
   * Toggle Dark Skin
   */
  toggleDarkSkin() {
    // Get the current skin
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.currentSkin = config.layout.skin;
      });

    // Toggle Dark skin with prevSkin skin
    this.prevSkin = localStorage.getItem('prevSkin');

    if (this.currentSkin === 'dark') {
      this._coreConfigService.setConfig(
        { layout: { skin: this.prevSkin ? this.prevSkin : 'default' } },
        { emitEvent: true }
      );
    } else {
      localStorage.setItem('prevSkin', this.currentSkin);
      this._coreConfigService.setConfig({ layout: { skin: 'dark' } }, { emitEvent: true });
    }
  }

  /**
   * Logout method
   */
  logout() {
    this._authenticationService.logout();
    // this._router.navigate(['/nft']);
    this._router.navigate(['/crypto']);
    const menu = this._coreMenuService.getMenu('nft');
    this.disableMenu(menu);
    this._coreMenuService.setCurrentMenu('nft');
    this.currentUser = null;
  }


  get getMenuStatus() : string {
    let url: string = this._router.url;
    if(url.includes('crypto')){
      return 'crypto';
    } else if(url.includes('nft')) {
      return 'nft';
    } else {
      return 'stock';
    }
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._authenticationService.currentUser.pipe(takeUntil(this._unsubscribeAll)).subscribe(x => {
      this.currentUser = x;
      if(new Object(this.currentUser).hasOwnProperty('user')){
        if(this.currentUser.user.profilePhoto !== ''){
          this.avatarImage = this.currentUser.user.profilePhoto;
        }

        if(this.currentUser.user.defaultProduct){
          let s: string = this.currentUser.user.defaultProduct
          this.defaultRouting = `/${s.toLowerCase()}`;
        }
      } else {
        this.defaultRouting = '/';
      }
    });

    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event:any) => {
      let url: string = event.url;
      if(url.includes('crypto')){
        this.menuStatus = 'crypto';
      } else if (url.includes('nft')) {
        this.menuStatus = 'nft';
      } else {
        this.menuStatus = 'stock';
      }
    });

    /* show navbar icon on specific routes */
    this._router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe((data)=>{
      if (data instanceof RoutesRecognized) {
        this.showIcon = data.state.root.firstChild.data.icon;
      }
    });

    // Subscribe to the config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
      this.horizontalMenu = config.layout.type === 'horizontal';
      this.hiddenMenu = config.layout.menu.hidden === true;
      this.currentSkin = config.layout.skin;

      // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
      if (this.coreConfig.layout.type === 'vertical') {
        setTimeout(() => {
          if (this.coreConfig.layout.navbar.type === 'fixed-top') {
            this.isFixed = true;
          }
        }, 0);
      }
    });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == 'horizontal') {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
        const isFixedTop = this._mediaObserver.isActive('bs-gt-xl');
        if (isFixedTop) {
          this.isFixed = false;
        } else {
          this.isFixed = true;
        }
      });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  gotoHelp(){
    const url = this._router.serializeUrl(
      this._router.createUrlTree([`/help/overview`])
    );
    window.open(url, '_blank');
  }

  disableMenu(menu){
    menu.forEach(element => {
      if(element.hasOwnProperty('disabled')){
        element.disabled = true;
      }
      if(element.hasOwnProperty('children')){
        this.disableMenu(element.children)
      }
    });

    return menu;
  }

  startTour(){
    this._shepherdService.start();
  }

  gotoSection(name){
    this._router.navigateByUrl(`/${name}`);
  }

  openLoginModal(){
    this.modalService.open(LoginComponent, {
      size: 'lg',
      centered: true
    })
  }

  openAboutUsModal(){
    this.modalService.open(AboutUsComponent, {
      size: 'xl',
      centered: true
    })
  }
}
